import { z } from 'zod';
import { CLAIM_STATUSES, CURRENCIES, EXPENSE_CATEGORIES, PAYMENT_METHODS } from '../enums.js';
import { optionalText, patchSchemaOf } from './common.js';
/**
 * Accounting — money going out. DECISIONS.md §14.
 *
 * NOT a ledger. MEFOSA's books live with their accountant (§2) and this does
 * not duplicate them: an expense here is the SUPPORTING DOCUMENT behind a
 * figure — what was spent, on what, for whom, with the supplier's M.O.F.
 * number and the receipt reference — so it can be exported and posted into her
 * own system.
 *
 * No VAT anywhere (rule 4).
 */
// ---------------------------------------------------------------- expenses
export const expenseInputSchema = z.object({
    spentOn: z.string().date(),
    category: z.enum(EXPENSE_CATEGORIES).default('other'),
    description: z.string().trim().min(2, 'Say what this was for').max(300),
    /** Minor units AND currency, together, always (rule 5). */
    amountMinor: z.number().int().positive('An expense of nothing is not an expense'),
    currency: z.enum(CURRENCIES).default('USD'),
    supplierId: z.string().uuid().nullable().optional(),
    /** Free text, for the taxi and the café that have no supplier record. */
    supplierName: optionalText,
    /** Their M.O.F. number from the receipt. Plain text: formats vary. */
    supplierMof: optionalText,
    /** The receipt or invoice number — what makes this evidence rather than a claim. */
    documentRef: optionalText,
    companyId: z.string().uuid().nullable().optional(),
    projectId: z.string().uuid().nullable().optional(),
    dealId: z.string().uuid().nullable().optional(),
    incurredBy: z.string().uuid().nullable().optional(),
    /** Paid out of pocket and owed back, rather than paid by MEFOSA directly. */
    reimbursable: z.boolean().default(false),
    notes: optionalText,
});
export const expenseUpdateSchema = patchSchemaOf(expenseInputSchema);
/**
 * Deciding a claim. Its own schema, its own route, its own permission.
 *
 * `claimStatus` is deliberately absent from the update schema above: folded in
 * there, anybody who could file a claim could approve their own.
 */
export const claimDecisionSchema = z.object({
    status: z.enum(['approved', 'rejected', 'reimbursed']),
    decisionNote: optionalText,
    /** Required when marking reimbursed: the date the money went back. */
    reimbursedOn: z.string().date().nullable().optional(),
});
// ------------------------------------------------------- supplier payments
export const purchasePaymentInputSchema = z.object({
    purchaseOrderId: z.string().uuid(),
    amountMinor: z.number().int().positive(),
    currency: z.enum(CURRENCIES),
    method: z.enum(PAYMENT_METHODS).default('bank_transfer'),
    paidOn: z.string().date(),
    reference: optionalText,
    notes: optionalText,
});
/**
 * Net cash for one currency.
 *
 * Claims that are approved-but-unpaid are deliberately NOT subtracted: the
 * money has not left yet, and treating a liability as a payment would
 * understate the balance. It is reported beside the net so a reader can take
 * it into account themselves.
 */
export function netCash(receivedMinor, refundedMinor, supplierPaidMinor, expensesMinor) {
    // A refund is money going back OUT to a client. Refunds live in their own
    // table (§16), so receivedMinor never already has them netted off.
    return receivedMinor - refundedMinor - supplierPaidMinor - expensesMinor;
}
/**
 * Adds amounts, refusing to mix currencies.
 *
 * Returns one line per currency rather than a total. Written as a function
 * because the alternative — a `reduce` that adds `amountMinor` — is exactly
 * the mistake it exists to prevent, and it reads as correct.
 */
export function totalByCurrency(rows) {
    const by = new Map();
    for (const r of rows)
        by.set(r.currency, (by.get(r.currency) ?? 0) + r.amountMinor);
    return [...by.entries()]
        .map(([currency, amountMinor]) => ({ currency, amountMinor }))
        .sort((a, b) => a.currency.localeCompare(b.currency));
}
/**
 * What is still owed on a purchase order, per currency.
 *
 * An order can be paid in instalments, so this is the order total less what
 * has been paid against it. Negative means overpaid, and is reported rather
 * than clamped: an overpayment is a real event somebody needs to see.
 */
export function outstandingOnOrder(totalMinor, paidMinor) {
    return totalMinor - paidMinor;
}
export { CLAIM_STATUSES };
//# sourceMappingURL=accounting.js.map